// frontend/src/utils/speakerDetector.js
// Open-source speaker detection using Web Audio API FFT.
// No external API keys needed. Works entirely in the browser.

export const STORAGE_KEY = 'voice_fingerprint_v1';
export const SIMILARITY_THRESHOLD = 0.91; // 0–1: higher = stricter match

// ── FFT settings ──────────────────────────────────────────────────────────────
// At 16kHz sample rate with FFT size 1024:
//   bin resolution = 16000/1024 ≈ 15.6 Hz per bin
//   Voice frequency range 80–3500 Hz  →  bins ~5 to ~224
export const FFT_SIZE       = 1024;
export const VOICE_BIN_LOW  = 5;
export const VOICE_BIN_HIGH = 224;

// ── Utilities ─────────────────────────────────────────────────────────────────

/** Convert dB FFT data (from AnalyserNode) to linear power in voice range */
export function extractVoiceBins(fftDbArray) {
  const bins = fftDbArray.slice(VOICE_BIN_LOW, VOICE_BIN_HIGH + 1);
  // dB → linear, floor at -100 dB
  return Float32Array.from(bins, db => Math.pow(10, Math.max(db, -100) / 20));
}

/** Normalize a vector to unit length */
export function normalize(vec) {
  const norm = Math.sqrt(vec.reduce((s, v) => s + v * v, 0));
  if (norm === 0) return vec;
  return Float32Array.from(vec, v => v / norm);
}

/** Cosine similarity between two unit-normalized vectors */
export function cosineSimilarity(a, b) {
  let dot = 0;
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) dot += a[i] * b[i];
  return dot; // already unit vectors → dot product = cosine similarity
}

// ── Fingerprint storage ───────────────────────────────────────────────────────

export function saveFingerprint(fingerprint) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(fingerprint)));
}

export function loadFingerprint() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return new Float32Array(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function clearFingerprint() {
  localStorage.removeItem(STORAGE_KEY);
}

export function hasFingerprint() {
  return !!localStorage.getItem(STORAGE_KEY);
}

// ── Enrollment: collect N frames → average → normalize ───────────────────────

/**
 * Record DURATION_MS audio, collect FFT frames every INTERVAL_MS,
 * return a normalized fingerprint vector.
 *
 * @param {MediaStream} stream  - from getUserMedia
 * @param {AudioContext} audioCtx
 * @param {number} durationMs
 * @param {(progress: number) => void} onProgress  0..1
 */
export async function buildFingerprint(stream, audioCtx, durationMs = 8000, onProgress) {
  const source   = audioCtx.createMediaStreamSource(stream);
  const analyser = audioCtx.createAnalyser();
  analyser.fftSize        = FFT_SIZE;
  analyser.smoothingTimeConstant = 0.3;
  source.connect(analyser);
  // Do NOT connect analyser to destination (silent)

  const frames   = [];
  const interval = 80; // ms between frames
  const total    = Math.floor(durationMs / interval);
  let   collected = 0;

  await new Promise((resolve) => {
    const timer = setInterval(() => {
      const data = new Float32Array(analyser.frequencyBinCount);
      analyser.getFloatFrequencyData(data);
      frames.push(extractVoiceBins(data));
      collected++;
      onProgress?.(collected / total);
      if (collected >= total) { clearInterval(timer); resolve(); }
    }, interval);
  });

  source.disconnect();

  // Average all frames
  const binCount    = frames[0].length;
  const fingerprint = new Float32Array(binCount);
  frames.forEach(frame => { frame.forEach((v, i) => { fingerprint[i] += v; }); });
  fingerprint.forEach((_, i) => { fingerprint[i] /= frames.length; });

  return normalize(fingerprint);
}

// ── Real-time detection ───────────────────────────────────────────────────────

/**
 * Check live FFT against stored fingerprint.
 * @returns true = user's voice, false = other voice, null = no fingerprint
 */
export function checkSpeaker(analyser, fingerprint) {
  if (!fingerprint) return null;
  const data = new Float32Array(analyser.frequencyBinCount);
  analyser.getFloatFrequencyData(data);
  const live = normalize(extractVoiceBins(data));
  const sim  = cosineSimilarity(live, fingerprint);
  return { isUser: sim >= SIMILARITY_THRESHOLD, similarity: sim };
}
